// The parenthesised `ALTER TABLE … ADD ( … )` group: which COLUMNS it
// declares, and with which spelling.
//
// The group itself stays an `opaque` clause — it is outside v1's narrow op
// set, the round-trip preserves its bytes, and every consumer that reads
// clause kinds keeps seeing exactly what it saw before. What it may NOT stay
// is anonymous: this is Oracle's ONLY multi-column ADD syntax (MySQL accepts
// the same shape), our own coalescer EMITS it on Oracle, and the three-origin
// contract in `ddl/apply/dialect.quoting.ts` asks every ALTER TABLE source the
// same question — was this column written DELIMITED? A `"Id"` declared here is
// otherwise indistinguishable from a bare `Id`, and every later edge naming
// that column folds it to `ID`: ORA-00904.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      ALTER TABLE / Index forms.

import type { ParseOptions } from '../../../sql-grammar/model';
import { readIdentifier } from '../../scope/identifiers';
import { findTopLevelCommas } from '../../structure/scanner';
import type { Identifier } from '../../types';

import { findMatchingParen, skipWsCommentsLocal } from './shared';

/** Constraint / index lead keywords that disqualify an `ADD …` / `DROP …`
 *  clause — or one element of an `ADD ( … )` group — from naming a column.
 *  An element led by one of these declares a constraint, so its own name
 *  (`ADD (CONSTRAINT "Pk" PRIMARY KEY (id))`) is not a column spelling. */
export const NON_COLUMN_LEAD_KEYWORDS: ReadonlySet<string> = new Set([
  'CONSTRAINT', 'PRIMARY', 'UNIQUE', 'FOREIGN', 'CHECK',
  'INDEX', 'KEY', 'FULLTEXT', 'SPATIAL', 'EXCLUDE', 'PERIOD',
]);

/** The columns a parenthesised `ADD ( … )` group leaves EXISTING, each paired
 *  with the spelling the source wrote it in. Elements that declare no column
 *  (constraints, and anything without a `<name> <type…>` shape) are skipped,
 *  so the list is positional only within itself — callers match by name.
 *
 *  `open` is the offset of the group's `(`. An UNTERMINATED group (the user is
 *  still typing) reads to `end` rather than yielding nothing: a half-written
 *  statement's earlier columns are as real as a closed one's. */
export function parenAddedColumns(
  source: string,
  options: ParseOptions,
  open: number,
  end: number,
): readonly Identifier[] {
  const close = findMatchingParen(source, options, open, end);
  const bodyEnd = close < 0 ? end : close;
  const bodyStart = open + 1;
  const out: Identifier[] = [];
  let segStart = bodyStart;
  for (const boundary of [...findTopLevelCommas(source, bodyStart, bodyEnd, options), bodyEnd]) {
    const ident = elementColumnName(source, options, segStart, boundary);
    if (ident) out.push(ident);
    segStart = boundary + 1;
  }
  return out;
}

/** The column name ONE element of the group declares, or null when it declares
 *  none. The shape test mirrors the single-column `ADD <name> <type>` path in
 *  `alterTable.clause.ts`: a lead identifier that is not a constraint keyword,
 *  followed by something (the type). A DELIMITED lead is never keyword-tested,
 *  for the same reason as there — delimiters make it a name. */
function elementColumnName(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): Identifier | null {
  const i = skipWsCommentsLocal(source, start, end, options);
  const lead = readIdentifier(source, i, end, options);
  if (!lead) return null;
  if (!lead.quoted && NON_COLUMN_LEAD_KEYWORDS.has(lead.value.toUpperCase())) return null;
  if (skipWsCommentsLocal(source, lead.end, end, options) >= end) return null;
  return { name: lead.value, quoting: lead.quoting };
}
